import type { WorkflowVersion } from '@cortex/types';
import { useId, type CSSProperties } from 'react';

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  flexWrap: 'wrap',
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
};

/**
 * Lets the reviewer re-point which version the diff compares against without
 * leaving the modal. The target (the card whose "View changes" was clicked)
 * stays fixed as the `to` side; only the baseline moves.
 *
 * Every other version is offered, including ones NEWER than the target: a
 * detached candidate is often most usefully compared against current mainline
 * rather than the version it forked from, and that mainline version can
 * outnumber it. Picking a newer baseline inverts the diff (additions read as
 * removals), which is the correct rendering of "what changes going from that
 * version to this one" — flagged inline so it doesn't read as a bug.
 *
 * Its own module rather than a private component in `VersionsPanel`: it needs
 * nothing from the panel (no query client, router, or mutations), so keeping it
 * separate makes the export intentional and lets it render in isolation.
 */
export function BaselinePicker({
  target,
  baseline,
  options,
  onChange,
}: {
  target: WorkflowVersion;
  baseline: number;
  options: readonly WorkflowVersion[];
  onChange: (versionNumber: number) => void;
}) {
  // `filter` already returns a fresh array, so sorting it in place is safe.
  const selectable = options
    .filter((v) => v.versionNumber !== target.versionNumber)
    .sort((a, b) => b.versionNumber - a.versionNumber);

  // Generated, not hard-coded: `id` is document-global and the modal portals to
  // `document.body`, so two mounted instances would both point their labels at
  // the first select and the second control would lose its accessible name.
  const selectId = useId();
  const warningId = useId();
  const inverted = baseline > target.versionNumber;

  return (
    <div style={rowStyle}>
      <label className="t-secondary t-xs" htmlFor={selectId}>
        Comparing <strong className="t-mono">v{target.versionNumber}</strong> against
      </label>
      {/* `.select-native` for the kit's hover/focus states and chevron, scaled
          down from its default 36px — oversized for a two-character label. The
          kit draws the chevron as a background image at `right 12px` and
          reserves `padding-right: 32px` for it, so both must be scaled
          together: a `padding` shorthand here would drop that reservation and
          let the label render underneath the chevron. Height matches
          `.btn--sm`. */}
      <select
        id={selectId}
        className="select-native"
        style={{
          width: 'auto',
          height: 28,
          fontSize: 'var(--fs-xs)',
          paddingLeft: 'var(--sp-2)',
          paddingRight: 'var(--sp-6)',
          backgroundPosition: 'right var(--sp-2) center',
        }}
        value={baseline}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-describedby={inverted ? warningId : undefined}
      >
        {selectable.map((v) => (
          <option key={v.id} value={v.versionNumber}>
            v{v.versionNumber}
            {/* Archived only. Archived versions are hidden from the cards behind
                this modal, so without the label their presence here reads as a
                bug. Detached ones are already visible in that list, so tagging
                them again would just be noise. */}
            {v.status === 'ARCHIVED' ? ' (archived)' : ''}
          </option>
        ))}
      </select>
      {/* Kept mounted with empty content rather than conditionally rendered: a
          live region inserted at the same moment its text appears is not
          reliably announced. This is the one place the diff's sign convention
          flips, so a screen-reader user picking a newer baseline needs to hear
          it — otherwise `+` lines announce as "added" when they are removals. */}
      <span id={warningId} role="status" className="t-secondary t-xs">
        {inverted ? 'Newer baseline — additions and removals read inverted.' : ''}
      </span>
    </div>
  );
}
